import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, ShieldAlert, ExternalLink } from "lucide-react";
import SEO from "../components/SEO";
import { findServiceAnywhere, servicePath } from "../data/services";
import { OUTCOME_DISCLAIMER } from "../data/legal";
import {
  GOALS,
  PATHS,
  recommend,
  NOT_ADVICE,
  RESULT_DISCLAIMER,
  OFFICIAL_CALCULATOR,
} from "../data/quiz";
import {
  submitApplication,
  appendApplicationAnswers,
  buildConsent,
} from "../config/maraApi";

const newSessionId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `s-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

const fade = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

export default function EligibilityQuiz() {
  const [phase, setPhase] = useState("triage"); // triage | branch | result | capture | done
  const [goal, setGoal] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // {key,question,answer,value}
  const [textValue, setTextValue] = useState("");
  const [result, setResult] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [sessionId] = useState(newSessionId);
  const [submitting, setSubmitting] = useState(false);

  const startGoal = (g) => {
    setGoal(g.key);
    setQuestions(PATHS[g.key] || []);
    setIdx(0);
    setAnswers([{ key: "goal", question: "What is your goal?", answer: g.label, value: g.key }]);
    setPhase("branch");
  };

  const recordAnswer = (q, label, value) => {
    setAnswers((prev) => [
      ...prev.filter((a) => a.key !== q.key),
      { key: q.key, question: q.question, answer: label, value },
    ]);
  };

  const advance = () => {
    if (idx < questions.length - 1) {
      setIdx(idx + 1);
      setTextValue("");
    } else {
      finishBranch();
    }
  };

  const onChoose = (q, opt) => {
    recordAnswer(q, opt.label, opt.value);
    advance();
  };

  const onTextNext = (q) => {
    const v = textValue.trim();
    if (q.optional && !v) return advance();
    if (!v) return;
    recordAnswer(q, v, v);
    advance();
  };

  const finishBranch = () => {
    // Use the freshest answers including any just-recorded.
    setAnswers((prev) => {
      const res = recommend(goal, prev);
      setResult(res);
      return prev;
    });
    setPhase("result");
  };

  const back = () => {
    if (phase === "branch" && idx > 0) {
      setIdx(idx - 1);
      setTextValue("");
    } else if (phase === "branch") {
      setPhase("triage");
      setGoal(null);
    } else if (phase === "capture") {
      setPhase("result");
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !privacy) return;
    setSubmitting(true);
    const consent = buildConsent({ privacyAccepted: privacy, marketingOptIn: marketing });
    const primary = result?.pathways?.[0]
      ? findServiceAnywhere(result.pathways[0])
      : null;
    try {
      const res = await submitApplication({
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        goal,
        visaGroup: primary?.group.slug || "",
        visa: result?.pathways?.[0] || "",
        answers,
        indicativeResult: result,
        priority: result?.priority || "normal",
        consent,
        sessionId,
      });
      // Append remains available for backend phase; harmless no-op now.
      if (res?.data?.id) {
        await appendApplicationAnswers(res.data.id, {
          answers,
          visaGroup: primary?.group.slug || "",
        }).catch(() => {});
      }
    } catch (err) {
      // Best effort, never block the user (D-005).
      // eslint-disable-next-line no-console
      console.error("quiz submit failed", err);
    } finally {
      setSubmitting(false);
      setPhase("done");
    }
  };

  const q = questions[idx];

  return (
    <>
      <SEO
        title="Check your eligibility"
        description="A free, indicative eligibility check for Australian visas. Answer a few questions and see the pathways worth exploring. Indicative guidance only, not personal advice."
        path="/eligibility-quiz/"
      />

      <section className="bg-acBg">
        <div className="container-page max-w-3xl py-12">
          {/* Triage */}
          {phase === "triage" && (
            <motion.div {...fade}>
              <p className="eyebrow">Eligibility check</p>
              <h1 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
                What are you hoping to do in Australia?
              </h1>
              <p className="mt-3 text-sm text-acBlack/60">{NOT_ADVICE}</p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {GOALS.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => startGoal(g)}
                    className="card flex flex-col items-start text-left transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <span className="font-semibold text-acblue">{g.label}</span>
                    <span className="mt-1 text-sm text-acBlack/60">{g.sub}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Branch questions */}
          {phase === "branch" && q && (
            <motion.div key={q.key} {...fade}>
              <div className="flex items-center justify-between">
                <button
                  onClick={back}
                  className="flex items-center gap-1 text-sm font-semibold text-acAccent"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <span className="text-xs text-acBlack/50">
                  Question {idx + 1} of {questions.length}
                </span>
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-acNavySoft">
                <div
                  className="h-full rounded-full bg-acblue transition-all"
                  style={{ width: `${((idx + 1) / questions.length) * 100}%` }}
                />
              </div>

              <h2 className="mt-8 text-2xl font-bold text-acblue">{q.question}</h2>
              {q.note && <p className="mt-2 text-sm text-acBlack/60">{q.note}</p>}

              {q.type === "choice" && (
                <div className="mt-6 grid gap-3">
                  {q.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => onChoose(q, opt)}
                      className="card flex items-center justify-between text-left transition hover:border-acblue hover:shadow-md"
                    >
                      <span className="text-acBlack/85">{opt.label}</span>
                      <ArrowRight className="h-4 w-4 text-acAccent" />
                    </button>
                  ))}
                  {q.optional && (
                    <button
                      onClick={advance}
                      className="mt-1 text-sm font-semibold text-acBlack/50 hover:text-acblue"
                    >
                      Skip this question
                    </button>
                  )}
                </div>
              )}

              {(q.type === "text" || q.type === "date") && (
                <form
                  className="mt-6"
                  onSubmit={(e) => {
                    e.preventDefault();
                    onTextNext(q);
                  }}
                >
                  <input
                    autoFocus
                    type={q.type === "date" ? "date" : "text"}
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    placeholder={q.placeholder || ""}
                    className="w-full rounded-xl border border-acblue/15 bg-white px-4 py-3 text-sm outline-none focus:border-acAccent"
                  />
                  <button type="submit" className="btn-primary mt-4">
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                  {q.optional && (
                    <button
                      type="button"
                      onClick={advance}
                      className="ml-3 text-sm font-semibold text-acBlack/50 hover:text-acblue"
                    >
                      Skip
                    </button>
                  )}
                </form>
              )}
            </motion.div>
          )}

          {/* Result */}
          {phase === "result" && result && (
            <motion.div {...fade}>
              <p className="eyebrow">Your indicative result</p>
              <h1 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
                {result.headline}
              </h1>
              <p className="mt-3 text-acBlack/70">{result.rationale}</p>

              {result.pointsBand && (
                <div className="mt-6 rounded-2xl bg-acNavySoft/60 p-5">
                  <p className="text-sm font-semibold text-acblue">
                    Indicative points band:{" "}
                    {result.pointsBand.min === result.pointsBand.max
                      ? `about ${result.pointsBand.min}`
                      : `${result.pointsBand.min} to ${result.pointsBand.max}`}{" "}
                    points
                  </p>
                  <p className="mt-2 text-xs text-acBlack/70">
                    A guide only, not a score. 65 points is just the minimum to enter the
                    pool. Real invitation cut-offs are higher and depend on your occupation.
                  </p>
                  <a
                    href={OFFICIAL_CALCULATOR}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-acAccent"
                  >
                    Check the official Department calculator
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}

              {result.pathways?.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-acAccent">
                    Pathways worth exploring
                  </h2>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {result.pathways.map((slug) => {
                      const r = findServiceAnywhere(slug);
                      if (!r) return null;
                      return (
                        <Link
                          key={slug}
                          to={servicePath(r.group, r.service)}
                          className="card group flex flex-col transition hover:-translate-y-0.5 hover:shadow-md"
                        >
                          <span className="text-xs font-semibold uppercase tracking-wide text-acAccent">
                            {r.group.title}
                          </span>
                          <span className="mt-1 font-semibold text-acblue">
                            {r.service.title}
                          </span>
                          <span className="mt-2 flex-1 text-sm text-acBlack/70">
                            {r.service.forWho}
                          </span>
                          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-acAccent">
                            Browse this visa
                            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {result.notes?.length > 0 && (
                <ul className="mt-8 space-y-2">
                  {result.notes.map((n, i) => (
                    <li key={i} className="flex gap-2 text-sm text-acBlack/75">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-acAccent" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-8 flex gap-3 rounded-2xl border border-acGold/40 bg-acGold/10 p-4">
                <ShieldAlert className="h-5 w-5 shrink-0 text-acGold" />
                <p className="text-xs text-acBlack/80">{RESULT_DISCLAIMER}</p>
              </div>

              <button onClick={() => setPhase("capture")} className="btn-primary mt-8">
                Email me my summary and book a consultation
                <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}

          {/* Capture */}
          {phase === "capture" && (
            <motion.div {...fade}>
              <button
                onClick={back}
                className="flex items-center gap-1 text-sm font-semibold text-acAccent"
              >
                <ArrowLeft className="h-4 w-4" /> Back to my result
              </button>
              <h1 className="mt-4 text-3xl font-bold text-acblue sm:text-4xl">
                Where should we send your summary?
              </h1>
              <p className="mt-3 text-acBlack/70">
                We will send your indicative summary and a link to book a free consultation
                with a Registered Migration Agent.
              </p>
              <form className="card mt-6 space-y-4" onSubmit={submitLead}>
                <input
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                  placeholder="Full name"
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  required
                />
                <input
                  type="email"
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                  placeholder="Email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  required
                />
                <input
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                  placeholder="Phone (include country code)"
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                />
                <label className="flex items-start gap-2 text-xs text-acBlack/70">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                    required
                  />
                  <span>
                    I have read the{" "}
                    <Link to="/privacy-policy/" className="text-acAccent underline">
                      Privacy Policy
                    </Link>{" "}
                    and consent to Migration & More collecting and using my personal
                    information to respond to my enquiry.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs text-acBlack/60">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                  />
                  <span>Optional: send me occasional updates about Australian visas.</span>
                </label>
                <button type="submit" className="btn-primary w-full" disabled={submitting}>
                  {submitting ? "Sending..." : "Send my summary"}
                </button>
                <p className="text-[11px] text-acBlack/50">{OUTCOME_DISCLAIMER}</p>
              </form>
            </motion.div>
          )}

          {/* Done */}
          {phase === "done" && (
            <motion.div {...fade} className="text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-acblue text-white">
                <Check className="h-7 w-7" />
              </div>
              <h1 className="mt-6 text-3xl font-bold text-acblue">
                Thanks{contact.name ? `, ${contact.name.split(" ")[0]}` : ""}.
              </h1>
              <p className="mx-auto mt-3 max-w-md text-acBlack/70">
                We have your details and will be in touch. The best next step is a
                consultation, where we give you honest advice for your situation.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/book-consultation/" className="btn-primary">
                  Book a consultation <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={OFFICIAL_CALCULATOR}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                >
                  Official points calculator
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}
